import { useCallback, useState } from 'react'
import { uploadEntityPhoto, IEntityPhotoPayload } from '../utils/uploadEntityPhoto'

export interface IPhotoUploadTarget {
  entityType: number
  entityId: number
}

export interface UsePhotoUploadReturn {
  /** null means the upload modal is closed. */
  target: IPhotoUploadTarget | null
  openPhotoUpload: (entityType: number, entityId: number) => void
  closePhotoUpload: () => void
  uploadPhoto: (payload: IEntityPhotoPayload) => Promise<void>
  isPhotoUploaded: (entityType: number, entityId: number) => boolean
}

// Type is part of the key: the same id may belong to different entities.
const buildKey = (entityType: number, entityId: number): string => `${entityType}:${entityId}`

/**
 * Owns the photo-upload target (entity type + id) and remembers which entities
 * already got a photo. Domain-agnostic — the caller decides what the numeric
 * type means. The marks live until the page reloads: there is no storage yet.
 */
export const usePhotoUpload = (): UsePhotoUploadReturn => {
  const [target, setTarget] = useState<IPhotoUploadTarget | null>(null)
  const [uploadedKeys, setUploadedKeys] = useState<Set<string>>(() => new Set())

  const openPhotoUpload = useCallback((entityType: number, entityId: number) => {
    setTarget({ entityType, entityId })
  }, [])

  const closePhotoUpload = useCallback(() => {
    setTarget(null)
  }, [])

  // Rejection reaches FileUploadPanel, which renders it as the error card.
  const uploadPhoto = useCallback(
    async (payload: IEntityPhotoPayload) => {
      if (!target) throw new Error('לא נבחרה שורה להעלאת תמונה')
      await uploadEntityPhoto(target.entityType, target.entityId, payload)
      setUploadedKeys((prev) => new Set(prev).add(buildKey(target.entityType, target.entityId)))
    },
    [target],
  )

  const isPhotoUploaded = useCallback(
    (entityType: number, entityId: number) => uploadedKeys.has(buildKey(entityType, entityId)),
    [uploadedKeys],
  )

  return { target, openPhotoUpload, closePhotoUpload, uploadPhoto, isPhotoUploaded }
}
